import React, { useRef } from "react";
import { Box, Typography, Button } from "@mui/material";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import entrance from "../assets/entrance.png";
import { useNavigate } from "react-router-dom";

const AboutCard = ({
  img = entrance,
  title = "About Us",
  paragraphs = [
    "At ARC Studioz, every project begins with a story one shaped by thoughtful design, precise engineering, and a commitment to craftsmanship that defines who we are. As a contemporary design-and-build firm, we offer a cohesive spectrum of services across Architecture, Interior Design and Contracting. Ensuring that every space we create is a seamless integration of form and function.",
    "Our unified approach is our biggest strength. By bringing design and execution under one roof, we eliminate the disconnect that often exists between planning and construction. This allows us to preserve design intent, maintain transparency, and deliver spaces that are completed on time, on budget, and without compromise. From spatial analysis to the final finish, our attention to detail ensures that each project whether a bespoke residence or a dynamic commercial environment carries the unmistakable ARC Studioz signature.",
    "We are a true one-stop solution for all interior and architectural needs, including electrical, Lighting, Plumbing, HVAC & CCTV. Every element is managed with precision, allowing our clients to experience a smooth, cohesive journey from concept to completion.",
  ],
}) => {
  const sectionRef = useRef(null);

  const nav = useNavigate();

  // Scroll tracking
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 80%", "end 10%"], // tune entry/exit
  });

  // Smooth motion
  const prog = useSpring(scrollYProgress, { stiffness: 80, damping: 20 });

  // Only vertical movement for image
  const innerY = useTransform(prog, [0, 1], ["-30%", "30%"]);

  return (
    <Box
      ref={sectionRef}
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "center",
        gap: 6,
        px: { xs: 3, md: 10 },
        py: { xs: 8, md: 12 },
        backgroundColor: "#fff",
      }}
    >
      {/* Image container with overflow hidden */}
      <Box
        sx={{
          width: { xs: "100%", md: "45vw" },
          maxWidth: 820,
          borderRadius: "16px",
          overflow: "hidden", // keeps motion inside outline
          position: "relative",
          boxShadow: "0 24px 50px -28px rgba(0,0,0,.35)",
        }}
      >
        {/* Moving image inside fixed frame */}
        <motion.img
          src={img}
          alt={title}
          style={{
            y: innerY,
            willChange: "transform",
            width: "100%",
            height: "auto",
            objectFit: "cover",
            display: "block",
          }}
        />
      </Box>

      {/* Static text */}
      <Box sx={{ maxWidth: 680 }}>
        <Typography
          variant="h3"
          sx={{
            color: "#b2925a",
            fontWeight: 600,
            fontFamily: "serif",
            mb: 2,
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>

        {paragraphs.map((p, i) => (
          <Typography key={i} sx={{ color: "#333", mb: 2, textAlign:"justify" }}>
            {p}
          </Typography>
        ))}

        <Button
          variant="outlined"
          sx={{
            borderColor: "#b2925a",
            color: "#b2925a",
            px: 4,
            py: 1,
            "&:hover": { backgroundColor: "#b2925a", color: "#fff" },
          }}
          onClick={()=>nav("/about")}
        >
            KNOW MORE
        </Button>
      </Box>
    </Box>
  );
};

export default AboutCard;
